<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { GalleryStorageSummary } from '@/types/galleries'
import { formatBytes } from '@/utils/formatBytes'

const props = defineProps<{
  summary: GalleryStorageSummary
}>()

const { t } = useI18n()

type BannerKind = 'over' | 'full' | 'warning'

const kind = computed<BannerKind | null>(() => {
  if (props.summary.is_over_quota) return 'over'
  if (props.summary.uploads_blocked) return 'full'
  if (props.summary.percent_used >= 80) return 'warning'
  return null
})

const message = computed(() => {
  switch (kind.value) {
    case 'over':
      return t('galleries.banner.over', {
        over: formatBytes(props.summary.used_bytes - props.summary.quota_bytes),
        quota: formatBytes(props.summary.quota_bytes),
      })
    case 'full':
      return t('galleries.banner.full')
    case 'warning':
      return t('galleries.banner.warning', { percent: Math.floor(props.summary.percent_used) })
    default:
      return ''
  }
})
</script>

<template>
  <div
    v-if="kind !== null"
    class="rounded-xl border px-5 py-4 text-sm"
    :class="kind === 'warning'
      ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
      : 'bg-red-500/10 border-red-500/30 text-red-300'"
    :role="kind === 'warning' ? 'status' : 'alert'"
    :data-kind="kind"
    data-testid="gallery-storage-banner"
  >
    {{ message }}
  </div>
</template>
